import React from 'react';
import { Alert, Link, Typography } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useSystemStatus } from '@/hooks/queries/admin/useSystemHealth';
import { useAdminConfigs } from '@/hooks/queries/admin/useAdminConfigs';

const DEFAULT_MESSAGE =
  'The eQuip Asset Management System is currently operating in a degraded state. Errors may occur when performing certain actions. Please contact eQuip support at https://support.equipsoftware.com/ for assistance.';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const renderMessageWithLinks = (text: string): React.ReactNode =>
  text.split(URL_REGEX).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <Link
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: 'inherit', fontWeight: 700, textDecorationColor: 'inherit' }}
      >
        {part}
      </Link>
    ) : (
      part
    ),
  );

export const DegradedHealthBanner: React.FC = () => {
  const { data: statusData } = useSystemStatus();
  const { data: configRows = [] } = useAdminConfigs('support_');

  if (!statusData || statusData.status === 'healthy') {
    return null;
  }

  const messageRow = configRows.find((r) => r.config_key === 'support_degraded_message');
  const message =
    typeof messageRow?.config_value === 'string' && messageRow.config_value.trim()
      ? messageRow.config_value
      : DEFAULT_MESSAGE;

  return (
    <Alert
      severity="warning"
      icon={<Warning sx={{ fontSize: 28 }} />}
      sx={{
        borderRadius: 0,
        py: 1.5,
        px: 3,
        bgcolor: 'warning.dark',
        color: 'warning.contrastText',
        '& .MuiAlert-icon': {
          color: 'warning.contrastText',
          alignItems: 'center',
        },
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Typography variant="body1" fontWeight={700}>
        System Alert
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
        {renderMessageWithLinks(message)}
      </Typography>
    </Alert>
  );
};
